<template>
  <div class="time-entries text-center">
    <TopMenu
      ref="topMenu"
      :title="$t('TIME_ENTRIES.PAGE_LABEL')"
      no-back/>
    <div
      :style="'padding-top: ' + weekSelectorHeight + 'px;'"
      class="container-fluid content">
      <div class="fixed-week-selector">
        <WeekSelector
          ref="weekSelector"
          v-model="week"
          @renderChanged="updateRender"
          @change="changeWeek"/>
      </div>
      <div class="result-wrapper">
        <div
          v-if="selectedWeekTimeEntries && selectedWeekTimeEntries.length === 0"
          class="no-result mt-4">
          <div
            :class="{'image-wrapper': !selectedProfilePictureIsDefault, 'image-wrapper-icon': selectedProfilePictureIsDefault}"
            class="d-inline-block align-middle mb-3">
            <i
              v-if="selectedProfilePictureIsDefault"
              class="fas fa-user"/>
            <img
              v-else
              :src="selectedProfilePictureString">
          </div>
          <p>{{ $t("TIME_ENTRIES.HI") }} <span class="font-weight-bold">{{ selectedMember.firstName }} {{ selectedMember.lastName }}</span>!</p>
          <hr class="linebreak">
          <p>{{ $t("TIME_ENTRIES.NO_RESULT_INSTRUCTIONS") }}</p>
        </div>
        <div
          v-if="selectedWeekTimeEntries && selectedWeekTimeEntries.length > 0"
          class="result">
          <SummaryTimeEntry
            v-for="(timeEntry, index) in selectedWeekTimeEntries"
            :key="index"
            :company-name="timeEntry.employer ? timeEntry.employer.companyName : $t('TIME_ENTRIES.UNSPECIFIED_EMPLOYER')"
            :activity-area="$t(timeEntry.activityArea ? timeEntry.activityArea.name : null)"
            :time="timeEntry.hasNotWorked ? $t('TIME_ENTRIES.I_HAVE_NOT_WORKED') : addTime(timeEntry.regularTime, timeEntry.overTime)"
            :date="timeEntry.date"
            :already-sent="timeEntry.isSubmitted"
            :id="timeEntry.id" />
        </div>
        <div
          v-if="selectedWeekTimeEntries && selectedWeekTimeEntries.length > 0"
          class="result">
          <TotalTimeEntry
            v-for="(timeEntry, index) in calcSummary(selectedWeekTimeEntries)"
            :key="index"
            :company-name="timeEntry.employer ? timeEntry.employer.companyName : $t('TIME_ENTRIES.UNSPECIFIED_EMPLOYER')"
            :time="timeEntry.hasNotWorked ? $t('TIME_ENTRIES.I_HAVE_NOT_WORKED') : addTime(timeEntry.regularTime, timeEntry.overTime)"
          />
        </div>
        <div class="result-wrapper">
          <div class="result">
            <div class="actions row">
              <div class="col-10">
                <ActionButton
                  v-if="selectedWeekTimeEntriesNotSubmitted && selectedWeekTimeEntriesNotSubmitted.length > 0"
                  class="btn btn-lg btn-primary btn-block"
                  @click="onSubmitHoursClick()">{{ $t("TIME_ENTRIES.SUBMIT_HOURS_BUTTON") }}</ActionButton>
              </div>
              <div class="col-2">
                <ActionButton
                  class="btn btn-secondary btn-icon d-inline-block"
                  @click="addHours"><i class="fas fa-plus"/></ActionButton>
              </div>
            </div>
          </div>
        </div>
        <ConfirmModal
          v-if="showConfirmSubmit"
          show-modal
          size="md"
          @cancel="showConfirmSubmit = false"
          @confirm="submit">{{ $t("TIME_ENTRIES.CONFIRM_MODAL_SUBMIT_TIME_ENTRY") }}</ConfirmModal>
      </div>
    </div>
    <BottomMenu />
  </div>
</template>

<script>
import moment from 'moment';
import { mapState } from 'vuex';
import TopMenu from '@/components/Menu/TopMenu';
import BottomMenu from '@/components/Menu/BottomMenu';
import TextLink from '@/components/Utils/TextLink';
import WeekSelector from '@/components/Date/WeekSelector';
import ActionButton from '@/components/Actions/ActionButton';
import SummaryTimeEntry from '@/components/Summary/SummaryTimeEntry';
import TotalTimeEntry from '@/components/Summary/TotalTimeEntry';
import ConfirmModal from '@/components/Modal/ConfirmModal';

export default {
  name: 'TimeEntries',
  components: {
    ActionButton,
    TextLink,
    TopMenu,
    BottomMenu,
    WeekSelector,
    SummaryTimeEntry,
    ConfirmModal,
    TotalTimeEntry,
  },
  data() {
    return {
      weekSelectorHeight: null,
      week: moment().week(),
      showConfirmSubmit: false,
    };
  },
  computed: {
    ...mapState('member', ['selectedMember', 'selectedProfilePictureString', 'selectedProfilePictureIsDefault']),
    ...mapState('timeEntry', [
      'selectedWeekTimeEntries',
      'selectedWeekTimeEntriesNotSubmitted',
    ]),
  },
  created() {
    this.$store.dispatch('member/currentMember').then(() => {
      this.$store.dispatch('timeEntry/currentWeekTimeEntries');
      this.$store.dispatch('employer/currentMemberEmployers');
    });
    this.$store.dispatch('member/currentProfilePicture');
  },
  mounted() {
    this.$nextTick(() => {
      window.addEventListener('resize', this.updateRender);
    });
    this.updateRender();
  },
  updated() {
    this.updateRender();
  },
  methods: {
    updateRender() {
      if (this.$refs.weekSelector && this.$refs.topMenu) {
        this.weekSelectorHeight = this.$refs.weekSelector.$el.offsetHeight
          + this.$refs.topMenu.$el.offsetHeight;
      }
    },
    calcSummary(array) {
      const tempresult = [];
      array.forEach((item) => {
        const tempcname = item.employer ? item.employer.id : 0;
        const workingItem = JSON.parse(JSON.stringify(item));
        workingItem.overTime = workingItem.hasNotWorked ? '0:00' : workingItem.overTime;
        workingItem.regularTime = workingItem.hasNotWorked ? '0:00' : workingItem.regularTime;
        let alreadythere = true;
        if (!tempresult[tempcname]) {
          alreadythere = false;
          tempresult[tempcname] = {};
        }
        if (!alreadythere) {
          tempresult[tempcname] = workingItem;
        } else {
          // eslint-disable-next-line max-len
          tempresult[tempcname].regularTime = this.addTime(workingItem.regularTime, tempresult[tempcname].regularTime, ':');
          // eslint-disable-next-line max-len
          tempresult[tempcname].overTime = this.addTime(workingItem.overTime, tempresult[tempcname].overTime, ':');
        }
      });
      const result = [];
      tempresult.forEach((item) => {
        result.push(item);
      });
      return result;
    },
    changeWeek(weekNumber) {
      this.$store.dispatch('timeEntry/rangeDateTimeEntries', {
        fromDate: moment()
          .week(weekNumber)
          .day(0)
          .format('YYYY-MM-DD[T]HH:mm:ss'),
        toDate: moment()
          .week(weekNumber)
          .day(6)
          .format('YYYY-MM-DD[T]HH:mm:ss'),
      });
    },
    addTime(time1, time2, returnsplitter = 'h') {
      const splitTime1 = time1.split(':');
      const splitTime2 = time2.split(':');

      let hours = parseInt(splitTime1[0], 10) + parseInt(splitTime2[0], 10);
      let minutes = parseInt(splitTime1[1], 10) + parseInt(splitTime2[1], 10);
      if (minutes > 59) {
        const hoursInMinutes = Math.floor(minutes / 60);
        hours += hoursInMinutes;
        minutes -= hoursInMinutes * 60;
      }
      if (minutes <= 9) {
        minutes = `0${minutes}`;
      }

      return `${hours}${returnsplitter}${minutes}`;
    },
    addHours() {
      this.$router.push('/dashboard/time-entries/add');
    },
    onSubmitHoursClick() {
      this.$store.dispatch('member/currentMember').then(() => {
        if (!this.selectedMember.hasContactInformation) {
          this.$store.commit('alerts/setAlert', {
            msg: 'TIME_ENTRIES.CONTACT_INFO_BEFORE_SUBMIT',
          });
          this.$router.push('/dashboard/profile/account/contact-information');
        } else {
          this.showConfirmSubmit = true;
        }
      });
    },
    submit() {
      this.showConfirmSubmit = false;
      this.$store
        .dispatch(
          'timeEntry/submit',
          moment()
            .week(this.week)
            .day(0)
            .format('YYYY-MM-DD[T]HH:mm:ss'),
        )
        .then(() => {
          this.$store.commit('alerts/setAlert', {
            msg: 'TIME_ENTRIES.CONFIRMATION_MESSAGE_SUBMIT',
          });
          this.changeWeek(this.week);
        });
    },
  },
};
</script>

<style scoped lang="scss">
div {
  &.time-entries {
    height: 100%;
  }
  &.content {
    padding-bottom: $bottom-menu-height;
    background-color: $base;
    min-height: 100%;
    padding-bottom: 136px;
  }
  &.fixed-week-selector {
    position: fixed;
    width: 100%;
    z-index: 1001;
    left: 0;
    top: $top-menu-height;
    background-color: $primary;
    border-bottom: 2px solid $primary;
  }
  &.actions {
    position: fixed;
    z-index: 1000;
    bottom: 75px;
    width: 100%;
    padding-left: 15px;
    padding-right: 15px;
  }

  &.image-wrapper {
    img {
      background-color: $base;
      border: solid 5px $secondary;
      width: 225px;
      height: 225px;
      border-radius: 50%;
      margin: 20px;

      object-fit: cover;
      object-position: center right;
    }
  }

  &.image-wrapper-icon {
    background-color: $base;
    border: solid 5px $secondary;
    border-radius: 50%;
    width: 225px;
    height: 225px;
    overflow: hidden;

    i {
      margin-top: 45px;
      font-size: 11rem;
      color: $primary;
    }
    img {
      background-color: $base;
      border: solid 5px $secondary;
      width: 225px;
      height: 225px;
      border-radius: 50%;
      margin: 20px;

      object-fit: cover;
      object-position: center right;
    }
  }
}

.btn-icon {
  color: $base !important;
  background-color: $primary !important;
  border-color: $primary !important;
}

.linebreak {
  width: 80px;
  height: 2px;
  background-color: $primary;
}
</style>
